/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getDeviceByDeviceId} from '@ion/core/src/services/Connect';
import {Asset} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseGetDeviceByDeviceIdResult = UseDefaultQueryResult & {
  result: Asset[];
};

export interface UseGetDeviceByDeviceIdParams {
  deviceId: number;
};

/**
 * Retrieves the device data from CMS side for the given Device ID.
 *
 * ```typescript
 *
 *  import {useGetDeviceByDeviceId} from '@ion/query/src/hooks/useGetDeviceByDeviceId';
 *
 *  const {result: device} = useGetDeviceByDeviceId({deviceId});
 *
 * ```
 */
export const useGetDeviceByDeviceId = ({
  deviceId
}: UseGetDeviceByDeviceIdParams,
enabled = true,
staleTime = getStaleTime(1, false, false)
): UseGetDeviceByDeviceIdResult => {
  const key   = createKey(QueryKey.DEVICE_BY_DEVICE_ID, deviceId);
  const query = useQuery(key, async() => {
    if (deviceId) {
      try {
        const result = await getDeviceByDeviceId(deviceId);
        return result;
      } catch (err) {
        error(err);
        return [];
      }
    }
    return [];
  }, {
    staleTime : staleTime as number,
    enabled
  });

  return createQueryResult(query, {
    result : query.data || []
  }) as UseGetDeviceByDeviceIdResult;
};

export default useGetDeviceByDeviceId;
